import restClient from 'restClient'
import type { StudioSystemType } from 'types/studio/items'

import { HttpError } from 'react-admin'
import type { RoofTypeName } from '../nativeMounting/types'
import { getRoofName } from '../nativeMounting/utils'
import { getVanDerValkPitchedCladdingOptionsFromSystem } from './applyVanDerValkPitchedCladdingOptions'
import {
  parseVanDerValkModuleLayout,
  type VanDerValkMiddleZoneDeflectorVendor,
} from './applyVanDerValkSuitabilityResultToSystem'
import { buildPitchedVendorFrameFromSuitabilityRow } from './buildPitchedVendorFrame'
import type { VanDerValkRoofPolygonPoint } from './buildVanDerValkPitchedRoofSlope'
import { normalizeSuitabilityResultPayload } from './expandVanDerValkSuitabilityResult'
import { mapVanDerValkBomApiErrorToUserMessage } from './mapVanDerValkBomApiErrorMessage'
import type { VanDerValkVendorBomLine } from './mapVanDerValkVendorBomToMountingItems'
import { getVanDerValkPitchedCladdingForRoofType } from './pitchedCladding'
import {
  isVanDerValkPitchedCladdingType,
  resolveStandingSeamTypeForBom,
  type VanDerValkPitchedCladdingType,
} from './pitchedCladdingFieldConfig'
import { enrichPitchedBomRowFromSuitabilityResult, VANDERVALK_PITCHED_VARIANT_FIELD_ORDER } from './suitabilityOptions'
import { isSolarRampAnchorFieldsComplete } from './solarRampAnchorFields'
import {
  isSolarRampFoundationTypeAllowed,
  isVanDerValkSolarRampSystem,
  resolveSolarRampPitchMm,
  resolveSolarRampVendorSystemType,
  type VanDerValkSolarRampFixation,
  type VanDerValkSolarRampFoundationType,
  type VanDerValkSolarRampMiddleZoneDeflector,
  type VanDerValkSolarRampVendorSystemType,
} from './solarRampProfiles'
import { isVanDerValkSuitabilityConfigRow, type VanDerValkSuitabilityConfig } from './types'
import {
  VDV_PITCHED_INCLINATION_MAX_DEG,
  VDV_PITCHED_INCLINATION_MIN_DEG,
  isVanDerValkPitchedInclinationValid,
} from './vanDerValkPitchedInclinationOverride'

export {
  VDV_PITCHED_INCLINATION_MAX_DEG,
  VDV_PITCHED_INCLINATION_MIN_DEG,
  isVanDerValkPitchedInclinationValid,
}

const api = restClient(`${window.API_ROOT}/api`)

export type FetchVanDerValkMountingBomResult =
  | { ok: true; bom: VanDerValkVendorBomLine[] }
  | { ok: false; message?: string }

function messageFromHttpErrorBody(body: unknown): string | undefined {
  if (body == null || typeof body !== 'object') {
    return undefined
  }
  const raw = (body as { message?: unknown }).message
  if (typeof raw === 'string' && raw.trim()) {
    return raw.trim()
  }
  return undefined
}

type VanDerValkIntegrationJson = Record<string, unknown> | undefined

function getVanDerValkIntegrationRecord(system: StudioSystemType | null | undefined): VanDerValkIntegrationJson {
  if (!system?.integration_json) {
    return undefined
  }
  const raw = (system.integration_json as { vanDerValk?: unknown }).vanDerValk
  if (raw == null || typeof raw !== 'object') {
    return undefined
  }
  return raw as Record<string, unknown>
}

function readVanDerValkBomInteger(
  vd: VanDerValkIntegrationJson,
  key: string,
  opts: { defaultValue: number; min: number; max?: number }
): number {
  if (!vd) {
    return opts.defaultValue
  }
  const raw = vd[key]
  if (raw === undefined || raw === null || raw === '') {
    return opts.defaultValue
  }
  const n = typeof raw === 'number' ? raw : Number(String(raw).trim())
  if (!Number.isFinite(n) || Number.isNaN(n)) {
    return opts.defaultValue
  }
  let x = Math.floor(n)
  x = Math.max(opts.min, x)
  if (opts.max !== undefined) {
    x = Math.min(opts.max, x)
  }
  return x
}

/** GB vendor ladder: 21.5 m/s … 31 m/s in 0.5 m/s steps → indices 0–19. */
const GB_BASIC_WIND_SPEED_MIN_MS = 21.5
const GB_BASIC_WIND_SPEED_MAX_IDX = 19

function readVanDerValkBomNumber(vd: VanDerValkIntegrationJson, key: string): number | null {
  if (!vd) {
    return null
  }
  const raw = vd[key]
  if (raw === undefined || raw === null || raw === '') {
    return null
  }
  const n = typeof raw === 'number' ? raw : Number(String(raw).trim())
  if (!Number.isFinite(n) || Number.isNaN(n)) {
    return null
  }
  return n
}

/** Map basic wind speed (m/s) to vendor `basicWindSpeedIdx` for GB (nearest 0.5 m/s step). Caller must pass a finite value. */
export function windSpeedMsToBasicWindSpeedIdx(ms: number): number {
  const idx = Math.round((ms - GB_BASIC_WIND_SPEED_MIN_MS) * 2)
  return Math.max(0, Math.min(GB_BASIC_WIND_SPEED_MAX_IDX, idx))
}

const VDV_ALTITUDE_MIN_M = -500
const VDV_ALTITUDE_MAX_M = 9000

export type VanDerValkBomInputErrorReason =
  | 'missing_wind_speed'
  | 'missing_altitude'
  | 'missing_topography_slope'
  | 'missing_topography_zone'
  | 'missing_solar_ramp_anchor_fields'

/** Wind speed (m/s) when set on integration_json; null when missing or invalid. */
export function readVanDerValkWindSpeedMsForBom(vd: VanDerValkIntegrationJson): number | null {
  return readVanDerValkBomNumber(vd, 'windSpeedMs')
}

/** Altitude AMSL (m) when set on integration_json; null when missing or out of vendor range. */
export function readVanDerValkAltitudeAboveSeaForBom(vd: VanDerValkIntegrationJson): number | null {
  const n = readVanDerValkBomNumber(vd, 'altitudeAboveSea')
  if (n == null || !Number.isFinite(n)) {
    return null
  }
  if (n < VDV_ALTITUDE_MIN_M || n > VDV_ALTITUDE_MAX_M) {
    return null
  }
  return n
}

/**
 * BOM input preconditions (wind, altitude, topography). Mirrors backend serializer rules.
 * Returns the first blocking reason, or null when all required inputs are present.
 */
export function getVanDerValkBomInputIssue(
  system: StudioSystemType | null | undefined
): VanDerValkBomInputErrorReason | null {
  const vd = getVanDerValkIntegrationRecord(system)
  if (readVanDerValkWindSpeedMsForBom(vd) == null) {
    return 'missing_wind_speed'
  }
  if (readVanDerValkAltitudeAboveSeaForBom(vd) == null) {
    return 'missing_altitude'
  }
  const topo = getVanDerValkTopographyForBom(system)
  if (topo == null) {
    const rawType = vd?.topography_type
    const topoType =
      typeof rawType === 'string' && (VDV_TOPOGRAPHY_TYPES as readonly string[]).includes(rawType)
        ? rawType
        : 'Not exposed'
    if (topoType === 'Escarpments or Cliffs') {
      const zone = vd?.topography_zone
      if (zone !== 'A' && zone !== 'B') {
        return 'missing_topography_zone'
      }
    }
    return 'missing_topography_slope'
  }
  if (isVanDerValkSolarRampSelectedOnSystem(system) && !isSolarRampAnchorFieldsComplete(vd ?? {})) {
    return 'missing_solar_ramp_anchor_fields'
  }
  return null
}

/** Studio default when unset (km); matches product default for vendor `countrySpecificData`. */
export const VDV_DEFAULT_DISTANCE_TO_SHORELINE_KM = 1

/** Studio default wind speed (m/s) when UK Dlubal fetch fails or omits wind_speed_ms. */
export const VDV_DEFAULT_WIND_SPEED_MS = 22

/** Studio default altitude AMSL (m) when UK Dlubal fetch fails or omits elevation. */
export const VDV_DEFAULT_ALTITUDE_ABOVE_SEA_M = 1

function readVanDerValkDistanceToShorelineKm(vd: VanDerValkIntegrationJson): number {
  const n = readVanDerValkBomNumber(vd, 'distanceToShorelineKM')
  if (n == null || !Number.isFinite(n)) {
    return VDV_DEFAULT_DISTANCE_TO_SHORELINE_KM
  }
  return Math.max(0, Math.min(10000, n))
}

/** Fixed vendor `project.designLifetime` (years); not exposed in Studio UI. */
export const VDV_BOM_DESIGN_LIFETIME_YEARS = 25

/** Fixed vendor field `inclinationOverride` (degrees) for flat-roof BOM; not exposed in Studio UI. */
export const VDV_BOM_INCLINATION_OVERRIDE_DEG = 0

/** GB flat-roof vendor `project.snowTopographyIdx` — only index 0 ("All cases") is supported; not exposed in Studio UI. */
export const VDV_GB_SNOW_TOPOGRAPHY_IDX = 0

/** Vendor ``fields[0].frame`` values from suitability Configuration (`selectedSuitability`). */
export type VanDerValkBomFrameFromSuitability = {
  panelType: 'NS' | 'EW'
  orientation: 'LANDSCAPE' | 'PORTRAIT'
  rowPitch: number
}

const ROW_PITCH_MM_MIN = 1
const ROW_PITCH_MM_MAX = 100_000

/** Row pitch in mm for vendor ``frame.pitch`` from suitability ``rowPitch`` (number or string, optional ``mm`` suffix). */
function parseVanDerValkRowPitchMm(row: VanDerValkSuitabilityConfig): number | null {
  const r = row as unknown as Record<string, unknown>
  const raw = row.rowPitch ?? r.RowPitch
  if (raw === undefined || raw === null) {
    return null
  }
  if (typeof raw === 'number') {
    const n = Math.round(raw)
    return n >= ROW_PITCH_MM_MIN && n <= ROW_PITCH_MM_MAX ? n : null
  }
  if (typeof raw === 'string') {
    const trimmed = raw
      .trim()
      .replace(/\s*mm\s*$/iu, '')
      .trim()
    if (trimmed === '') {
      return null
    }
    const parsed = parseFloat(trimmed)
    if (!Number.isFinite(parsed)) {
      return null
    }
    const n = Math.round(parsed)
    return n >= ROW_PITCH_MM_MIN && n <= ROW_PITCH_MM_MAX ? n : null
  }
  return null
}

/**
 * Map ``integration_json.vanDerValk.selectedSuitability`` to vendor ``panelType`` / ``orientation`` / ``rowPitch``.
 * ``placeSystem`` must be NS or EW; ``moduleOrientation`` must parse to landscape or portrait; ``rowPitch`` must be a
 * valid pitch in mm (1–100000).
 */
export function getVanDerValkBomFrameFromSystem(
  system: StudioSystemType | null | undefined
): VanDerValkBomFrameFromSuitability | null {
  const vd = getVanDerValkIntegrationRecord(system)
  const raw = vd?.selectedSuitability
  if (raw == null || typeof raw !== 'object' || !isVanDerValkSuitabilityConfigRow(raw)) {
    return null
  }
  const row = raw as VanDerValkSuitabilityConfig
  const place = String(row.placeSystem ?? '')
    .trim()
    .toUpperCase()
  if (place !== 'NS' && place !== 'EW') {
    return null
  }
  const layout = parseVanDerValkModuleLayout(row.moduleOrientation)
  if (layout !== 'landscape' && layout !== 'portrait') {
    return null
  }
  const rowPitchMm = isVanDerValkSolarRampSystem(row.system)
    ? resolveSolarRampPitchMm(row.system)
    : parseVanDerValkRowPitchMm(row)
  if (rowPitchMm == null) {
    return null
  }
  const orientation = layout === 'portrait' ? 'PORTRAIT' : 'LANDSCAPE'
  return { panelType: place, orientation, rowPitch: rowPitchMm }
}

/** Vendor ``fields[0].frame.middleZoneDeflector`` from suitability frame + integration (NS landscape, EW landscape). */
export function getVanDerValkMiddleZoneDeflectorForBom(
  system: StudioSystemType | null | undefined,
  frame: VanDerValkBomFrameFromSuitability
): VanDerValkMiddleZoneDeflectorVendor {
  const vd = getVanDerValkIntegrationRecord(system)
  const selected = vd?.selectedSuitability
  if (
    selected != null &&
    typeof selected === 'object' &&
    isVanDerValkSuitabilityConfigRow(selected) &&
    isVanDerValkSolarRampSystem(selected.system)
  ) {
    const v = vd?.middleZoneDeflector
    if (v === 'OPEN_SYSTEM' || v === 'CLOSED_SYSTEM') {
      return v
    }
    return 'OPEN_SYSTEM'
  }
  if (frame.panelType === 'NS') {
    if (frame.orientation === 'PORTRAIT') {
      return 'CLOSED_SYSTEM'
    }
    const v = vd?.middleZoneDeflector
    if (v === 'ONLY_BACKPLATES' || v === 'CLOSED_SYSTEM') {
      return v
    }
    return 'ONLY_BACKPLATES'
  }
  if (frame.orientation === 'PORTRAIT') {
    return 'CLOSED_SYSTEM'
  }
  const vEw = vd?.middleZoneDeflector
  if (vEw === 'OPEN_SYSTEM' || vEw === 'CLOSED_SYSTEM') {
    return vEw
  }
  return 'OPEN_SYSTEM'
}

const VDV_TOPOGRAPHY_TYPES = ['Not exposed', 'Hills or Ridge', 'Escarpments or Cliffs'] as const
const VDV_TOPOGRAPHY_SLOPES = ['10%', '20%', '>30%'] as const

/** MCS UK strings for Django ``get_topography_correction_factor`` / flat-roof BOM POST. */
export type VanDerValkTopographyBomFields = {
  topography_type: (typeof VDV_TOPOGRAPHY_TYPES)[number]
  topography_zone?: string
  topography_slope?: string
}

/**
 * Topography fields for BOM POST when integration_json satisfies backend rules; null otherwise.
 */
export function getVanDerValkTopographyForBom(
  system: StudioSystemType | null | undefined
): VanDerValkTopographyBomFields | null {
  const vd = getVanDerValkIntegrationRecord(system)
  const rawType = vd?.topography_type
  const topography_type = (
    typeof rawType === 'string' && (VDV_TOPOGRAPHY_TYPES as readonly string[]).includes(rawType)
      ? rawType
      : 'Not exposed'
  ) as VanDerValkTopographyBomFields['topography_type']

  if (topography_type === 'Not exposed') {
    return { topography_type }
  }

  const rawSlope = vd?.topography_slope
  if (typeof rawSlope !== 'string' || !(VDV_TOPOGRAPHY_SLOPES as readonly string[]).includes(rawSlope)) {
    return null
  }
  const topography_slope = rawSlope as VanDerValkTopographyBomFields['topography_slope']

  if (topography_type === 'Hills or Ridge') {
    return { topography_type, topography_slope }
  }

  const rawZone = vd?.topography_zone
  if (rawZone !== 'A' && rawZone !== 'B') {
    return null
  }
  return { topography_type, topography_zone: rawZone, topography_slope }
}

const VDV_DEFAULT_PARAPET_HEIGHT_M = 1
const VDV_PARAPET_HEIGHT_MIN_M = 0
const VDV_PARAPET_HEIGHT_MAX_M = 50

/** Pitched-roof vendor ``countrySpecificData.factors`` defaults (mounting tab numeric inputs). */
export const VDV_DEFAULT_ROUGHNESS_FACTOR = 0.8
export const VDV_ROUGHNESS_FACTOR_MIN = 0.01
export const VDV_ROUGHNESS_FACTOR_MAX = 10

export const VDV_DEFAULT_TURBULENCE_INTENSITY = 0.21
const VDV_TURBULENCE_INTENSITY_MIN = 0.01
const VDV_TURBULENCE_INTENSITY_MAX = 1

function readVanDerValkParapetHeightM(vd: VanDerValkIntegrationJson): number {
  const n = readVanDerValkBomNumber(vd, 'parapetHeightM')
  if (n == null || !Number.isFinite(n)) {
    return VDV_DEFAULT_PARAPET_HEIGHT_M
  }
  return Math.min(VDV_PARAPET_HEIGHT_MAX_M, Math.max(VDV_PARAPET_HEIGHT_MIN_M, n))
}

function readVanDerValkRoughnessFactor(vd: VanDerValkIntegrationJson): number {
  const n = readVanDerValkBomNumber(vd, 'roughnessFactor')
  if (n == null || !Number.isFinite(n) || n <= 0) {
    return VDV_DEFAULT_ROUGHNESS_FACTOR
  }
  return Math.min(VDV_ROUGHNESS_FACTOR_MAX, Math.max(VDV_ROUGHNESS_FACTOR_MIN, n))
}

function readVanDerValkTurbulenceIntensity(vd: VanDerValkIntegrationJson): number {
  const n = readVanDerValkBomNumber(vd, 'turbulenceIntensity')
  if (n == null || !Number.isFinite(n) || n <= 0) {
    return VDV_DEFAULT_TURBULENCE_INTENSITY
  }
  return Math.min(VDV_TURBULENCE_INTENSITY_MAX, Math.max(VDV_TURBULENCE_INTENSITY_MIN, n))
}

/** Pitched-roof wind factor fields for vendor ``countrySpecificData.factors``. */
export type VanDerValkPitchedRoofWindFactors = {
  roughnessFactor: number
  turbulenceIntensity: number
}

export function getVanDerValkPitchedRoofWindFactors(
  system: StudioSystemType | null | undefined
): VanDerValkPitchedRoofWindFactors {
  const vd = getVanDerValkIntegrationRecord(system)
  return {
    roughnessFactor: readVanDerValkRoughnessFactor(vd),
    turbulenceIntensity: readVanDerValkTurbulenceIntensity(vd),
  }
}

/** Project index fields for flat-roof BOM (vendor `project.*Idx` keys). */
export type VanDerValkFlatRoofBomProjectIndices = {
  kFIIdx: number
  terrainCategoryIdx: number
  snowTopographyIdx: number
  snowZoneIdx: number
  basicWindSpeedIdx: number
  altitudeAboveSea: number
  distanceToShorelineKM: number
  designLifetime: number
  parapetHeightM: number
  inclinationOverride: number
}

/**
 * Project index fields for BOM POST. Call only after ``getVanDerValkBomInputIssue`` returns null.
 */
export function getVanDerValkFlatRoofBomProjectIndices(
  system: StudioSystemType | null | undefined
): VanDerValkFlatRoofBomProjectIndices {
  const vd = getVanDerValkIntegrationRecord(system)
  const windMs = readVanDerValkWindSpeedMsForBom(vd)
  const altitudeAboveSea = readVanDerValkAltitudeAboveSeaForBom(vd)
  if (windMs == null || altitudeAboveSea == null) {
    throw new Error('getVanDerValkFlatRoofBomProjectIndices called without required wind speed and altitude')
  }
  return {
    kFIIdx: readVanDerValkBomInteger(vd, 'kFIIdx', { defaultValue: 0, min: 0, max: 2 }),
    terrainCategoryIdx: readVanDerValkBomInteger(vd, 'terrainCategoryIdx', { defaultValue: 0, min: 0, max: 2 }),
    snowTopographyIdx: VDV_GB_SNOW_TOPOGRAPHY_IDX,
    snowZoneIdx: readVanDerValkBomInteger(vd, 'snowZone', { defaultValue: 0, min: 0, max: 5 }),
    basicWindSpeedIdx: windSpeedMsToBasicWindSpeedIdx(windMs),
    altitudeAboveSea,
    distanceToShorelineKM: readVanDerValkDistanceToShorelineKm(vd),
    designLifetime: VDV_BOM_DESIGN_LIFETIME_YEARS,
    parapetHeightM: readVanDerValkParapetHeightM(vd),
    inclinationOverride: VDV_BOM_INCLINATION_OVERRIDE_DEG,
  }
}

export type VanDerValkBomFieldLocationPointPayload = {
  x: number
  y: number
  z: number
}

export type VanDerValkBomFieldLocationPayload = {
  base: VanDerValkBomFieldLocationPointPayload
  azimuth: VanDerValkBomFieldLocationPointPayload
  tangent: VanDerValkBomFieldLocationPointPayload
}

export type VanDerValkBomFieldPayload = {
  frames: number[][]
  fieldLabel?: string
  location: VanDerValkBomFieldLocationPayload
}

/** Pitched-roof vendor ``fields[]`` entry; ``inclinationOverride`` is per field. */
export type VanDerValkPitchedBomFieldPayload = VanDerValkBomFieldPayload & {
  inclinationOverride: number
}

export type VanDerValkBuildingOutlinePointPayload = {
  x: number
  y: number
}

/** Optional Solar Ramp vendor frame fields (sent when ``systemType`` is present). */
export type VanDerValkSolarRampBomRequestFields = {
  systemType: VanDerValkSolarRampVendorSystemType
  foundationType: VanDerValkSolarRampFoundationType
  edgeCornerZoneDeflector: VanDerValkSolarRampMiddleZoneDeflector
  fixation?: VanDerValkSolarRampFixation
  anchorType?: 'VALK_SOLAR_FIX' | 'OTHER'
  fastenerType?: string
  roofMaterial?: string
  roofConstructionHeight?: number
}

export function isVanDerValkSolarRampSelectedOnSystem(system: StudioSystemType | null | undefined): boolean {
  const vd = getVanDerValkIntegrationRecord(system)
  const raw = vd?.selectedSuitability
  if (raw == null || typeof raw !== 'object' || !isVanDerValkSuitabilityConfigRow(raw)) {
    return false
  }
  return isVanDerValkSolarRampSystem(raw.system)
}

/**
 * Solar Ramp vendor frame fields from ``integration_json.vanDerValk`` + suitability selection.
 */
export function getVanDerValkSolarRampBomFieldsFromSystem(
  system: StudioSystemType | null | undefined
): VanDerValkSolarRampBomRequestFields | null {
  const vd = getVanDerValkIntegrationRecord(system)
  const raw = vd?.selectedSuitability
  if (raw == null || typeof raw !== 'object' || !isVanDerValkSuitabilityConfigRow(raw)) {
    return null
  }
  const systemType = resolveSolarRampVendorSystemType(raw.system)
  if (!systemType) {
    return null
  }
  const middleZone = getVanDerValkMiddleZoneDeflectorForBom(system, {
    panelType: String(raw.placeSystem ?? '')
      .trim()
      .toUpperCase() as 'NS' | 'EW',
    orientation:
      parseVanDerValkModuleLayout(raw.moduleOrientation) === 'portrait' ? 'PORTRAIT' : 'LANDSCAPE',
    rowPitch: resolveSolarRampPitchMm(raw.system) ?? 0,
  })
  if (middleZone !== 'OPEN_SYSTEM' && middleZone !== 'CLOSED_SYSTEM') {
    return null
  }
  const foundationRaw = vd?.foundationType
  const foundationType =
    typeof foundationRaw === 'string' && isSolarRampFoundationTypeAllowed(raw.system, foundationRaw)
      ? foundationRaw
      : null
  if (!foundationType) {
    return null
  }
  const fixationRaw = vd?.flatRoofFixation
  const fixation: VanDerValkSolarRampFixation | undefined =
    fixationRaw === 'BALLASTING' || fixationRaw === 'ANCHORING' ? fixationRaw : undefined

  const fields: VanDerValkSolarRampBomRequestFields = {
    systemType,
    foundationType,
    edgeCornerZoneDeflector: middleZone,
    ...(fixation ? { fixation } : {}),
  }

  if (fixation === 'ANCHORING') {
    const anchorType = vd?.anchorType
    if (anchorType === 'VALK_SOLAR_FIX' || anchorType === 'OTHER') {
      fields.anchorType = anchorType
    }
    if (anchorType === 'VALK_SOLAR_FIX') {
      const fastenerType = typeof vd?.fastenerType === 'string' ? vd.fastenerType : undefined
      const roofMaterial = typeof vd?.roofMaterial === 'string' ? vd.roofMaterial : undefined
      const heightRaw = vd?.roofConstructionHeight
      const roofConstructionHeight =
        typeof heightRaw === 'number'
          ? heightRaw
          : typeof heightRaw === 'string'
            ? parseInt(heightRaw.trim(), 10)
            : NaN
      if (fastenerType) {
        fields.fastenerType = fastenerType
      }
      if (roofMaterial) {
        fields.roofMaterial = roofMaterial
      }
      if (Number.isFinite(roofConstructionHeight)) {
        fields.roofConstructionHeight = roofConstructionHeight
      }
    }
  }

  return fields
}

export type VanDerValkFlatRoofBomRequestBody = {
  width: number
  length: number
  thickness: number
  moduleWeightKg: number
  roofTypeName: RoofTypeName
  panelType: VanDerValkBomFrameFromSuitability['panelType']
  orientation: VanDerValkBomFrameFromSuitability['orientation']
  rowPitch: VanDerValkBomFrameFromSuitability['rowPitch']
  middleZoneDeflector: VanDerValkMiddleZoneDeflectorVendor
  fields: VanDerValkBomFieldPayload[]
  buildingOutline: VanDerValkBuildingOutlinePointPayload[]
} & VanDerValkFlatRoofBomProjectIndices &
  VanDerValkTopographyBomFields &
  Partial<VanDerValkSolarRampBomRequestFields>

/** Shared vendor ``project.*`` index fields for flat- and pitched-roof BOM POST bodies. */
export type VanDerValkBomProjectIndices = {
  kFIIdx: number
  terrainCategoryIdx: number
  snowTopographyIdx: number
  snowZoneIdx: number
  basicWindSpeedIdx: number
  altitudeAboveSea: number
  designLifetime: number
  distanceToShorelineKM: number
}

/** Project index fields shared by flat- and pitched-roof BOM endpoints. */
export function getVanDerValkBomProjectIndices(
  system: StudioSystemType | null | undefined
): VanDerValkBomProjectIndices {
  const indices = getVanDerValkFlatRoofBomProjectIndices(system)
  return {
    kFIIdx: indices.kFIIdx,
    terrainCategoryIdx: indices.terrainCategoryIdx,
    snowTopographyIdx: indices.snowTopographyIdx,
    snowZoneIdx: indices.snowZoneIdx,
    basicWindSpeedIdx: indices.basicWindSpeedIdx,
    altitudeAboveSea: indices.altitudeAboveSea,
    designLifetime: indices.designLifetime,
    distanceToShorelineKM: indices.distanceToShorelineKM,
  }
}

/** Vendor ``fields[].frame`` object for pitched-roof BOM. */
export type VanDerValkPitchedVendorFrame = Record<string, string | number | boolean | string[]>

/**
 * Map ``integration_json.vanDerValk.selectedSuitability`` to the vendor pitched ``frame`` object.
 */
export function getVanDerValkPitchedBomFrameFromSystem(
  system: StudioSystemType | null | undefined,
  claddingType?: VanDerValkPitchedCladdingType | null
): VanDerValkPitchedVendorFrame | null {
  const vd = getVanDerValkIntegrationRecord(system)
  const raw = vd?.selectedSuitability
  if (raw == null || typeof raw !== 'object' || !isVanDerValkSuitabilityConfigRow(raw)) {
    return null
  }

  let resolvedCladding = claddingType ?? null
  if (!resolvedCladding) {
    const roofTypeUrl = window.projectForm?.getState?.()?.values?.roof_type
    const roofTypeName = getRoofName(roofTypeUrl)
    const derived = getVanDerValkPitchedCladdingForRoofType(roofTypeName)
    if (derived && isVanDerValkPitchedCladdingType(derived)) {
      resolvedCladding = derived
    }
  }
  if (!resolvedCladding) {
    return null
  }

  const suitabilityResult = vd?.suitabilityResult
  let resultArray: VanDerValkSuitabilityConfig[] = []
  if (suitabilityResult != null) {
    const normalized = normalizeSuitabilityResultPayload(suitabilityResult)
    resultArray =
      normalized ?? (Array.isArray(suitabilityResult) ? (suitabilityResult as VanDerValkSuitabilityConfig[]) : [])
  }
  const row = enrichPitchedBomRowFromSuitabilityResult(raw as VanDerValkSuitabilityConfig, resultArray, {
    seamType: resolveStandingSeamTypeForBom(
      getVanDerValkPitchedCladdingOptionsFromSystem(system),
      typeof vd?.suitabilitySeamType === 'string' ? vd.suitabilitySeamType : null
    ),
    fieldOrder: VANDERVALK_PITCHED_VARIANT_FIELD_ORDER,
  })

  const frame = buildPitchedVendorFrameFromSuitabilityRow(row, resolvedCladding)
  return frame
}

export type VanDerValkPitchedRoofBomRequestBody = {
  width: number
  length: number
  thickness: number
  moduleWeightKg: number
  claddingType: string
  claddingOptions: Record<string, string | number>
  frame: VanDerValkPitchedVendorFrame
  roofPolygon: VanDerValkRoofPolygonPoint[]
  ridgeIdxs: number[]
  roofType: string
  fields: VanDerValkPitchedBomFieldPayload[]
} & VanDerValkBomProjectIndices &
  VanDerValkPitchedRoofWindFactors &
  VanDerValkTopographyBomFields

async function postVanDerValkBomRequest(
  url: string,
  body: Record<string, unknown>
): Promise<FetchVanDerValkMountingBomResult> {
  try {
    const { data } = await api('CUSTOM_POST', 'custom', { url, data: body })
    const d = data as { success?: boolean; bom?: unknown; message?: string }
    if (!d?.success || !Array.isArray(d.bom)) {
      return {
        ok: false,
        message:
          mapVanDerValkBomApiErrorToUserMessage(d?.message) ??
          window.translate?.('mount_vdv_bom_api_failed') ??
          'BOM request failed',
      }
    }
    return { ok: true, bom: d.bom as VanDerValkVendorBomLine[] }
  } catch (e) {
    if (e instanceof HttpError) {
      const fromBody = messageFromHttpErrorBody(e.body)
      if (fromBody) {
        return { ok: false, message: mapVanDerValkBomApiErrorToUserMessage(fromBody) }
      }
    }
    return {
      ok: false,
      message: e instanceof Error ? e.message : undefined,
    }
  }
}

function vanDerValkBomProjectContext():
  | { ok: true; orgId: number; projectId: number }
  | { ok: false; message: string } {
  const project = window.projectForm?.getState?.()?.values
  const orgId = project?.org_id
  const projectId = project?.id
  if (orgId == null || projectId == null) {
    return { ok: false, message: 'missing project context' }
  }
  return { ok: true, orgId, projectId }
}

/**
 * Flat-roof merged BOM: single POST to `.../vandervalk/flat-roof/bom/`. Django returns `{ success, bom }`.
 */
export async function fetchVanDerValkMountingBom(
  body: VanDerValkFlatRoofBomRequestBody
): Promise<FetchVanDerValkMountingBomResult> {
  const ctx = vanDerValkBomProjectContext()
  if (!ctx.ok) {
    return { ok: false, message: ctx.message }
  }

  const url = `orgs/${ctx.orgId}/projects/${ctx.projectId}/vandervalk/flat-roof/bom/`
  return postVanDerValkBomRequest(url, body as unknown as Record<string, unknown>)
}

/**
 * Pitched-roof BOM: single POST to `.../vandervalk/pitched-roof/bom/`. Django returns `{ success, bom }`.
 */
export async function fetchVanDerValkPitchedRoofMountingBom(
  body: VanDerValkPitchedRoofBomRequestBody
): Promise<FetchVanDerValkMountingBomResult> {
  const ctx = vanDerValkBomProjectContext()
  if (!ctx.ok) {
    return { ok: false, message: ctx.message }
  }

  const url = `orgs/${ctx.orgId}/projects/${ctx.projectId}/vandervalk/pitched-roof/bom/`
  return postVanDerValkBomRequest(url, body as unknown as Record<string, unknown>)
}

/** Merge pitched-roof BOM lines from multiple facet requests (same articleCode sums amount). */
export function mergeVanDerValkPitchedRoofBomResults(
  results: FetchVanDerValkMountingBomResult[]
): FetchVanDerValkMountingBomResult {
  const merged = new Map<string, VanDerValkVendorBomLine>()
  for (const res of results) {
    if (!res.ok) {
      return res
    }
    for (const line of res.bom) {
      const prev = merged.get(line.articleCode)
      if (prev) {
        merged.set(line.articleCode, {
          ...prev,
          amount: prev.amount + line.amount,
        })
      } else {
        merged.set(line.articleCode, { ...line })
      }
    }
  }
  return { ok: true, bom: [...merged.values()] }
}
